import axios, {AxiosError, AxiosRequestConfig, AxiosResponse, isAxiosError} from 'axios';

import {doLogout} from '@entities/user/model';
import {TokensData} from '@entities/user/types';
import {TokenStorage} from '@shared/lib/auth/tokens';

export const baseURL = '/b2b/resource';
export const api = axios.create({
    baseURL,
    headers: {'Content-Type': 'application/json'},
});

const refresh = async (token?: string) =>
    axios.post<TokensData>(`${baseURL}/uaa/auth/refresh`, token);

export async function updateTokens() {
    const refreshToken = TokenStorage.refreshToken;
    if (!refreshToken) {
        TokenStorage.removeTokens();
        doLogout(false);
        throw new Error('Refresh token is missing');
    }

    if (isRefreshing) {
        return new Promise<string | null>((resolve, reject) => {
            failedQueue.push({resolve, reject});
        });
    }

    isRefreshing = true;

    try {
        const response = await refresh(refreshToken);
        TokenStorage.storeTokens(response.data);
        const newAccessToken = TokenStorage.accessToken;

        isRefreshing = false;
        processQueue(null, newAccessToken);

        return newAccessToken;
    } catch (err) {
        isRefreshing = false;
        processQueue(err, null);
        TokenStorage.removeTokens();
        doLogout(false);
        throw err;
    }
}

export async function updateTokensBeforeExpire() {
    // const date = new Date(TokenStorage.accessTokenExpiresIn?.toString() ?? new Date());
    // if (differenceInMinutes(date, new Date()) < minutes) await updateTokens();
    await updateTokens();
}

api.interceptors.request.use((config) => {
    const token = TokenStorage.accessToken;
    if (token && config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

let isRefreshing = false;
let failedQueue: {
    resolve: (value: string | null) => void;
    reject: (error?: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((request) => {
        if (error) request.reject(error);
        else request.resolve(token);
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {_retry?: boolean};

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({resolve, reject});
                })
                    .then((token) => {
                        if (originalRequest.headers)
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        return api(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            isRefreshing = true;

            const refreshToken = TokenStorage.refreshToken;
            if (!refreshToken) {
                isRefreshing = false;
                return Promise.reject(error);
            }

            try {
                TokenStorage.removeTokens();
                const response = await refresh(refreshToken);
                TokenStorage.storeTokens(response.data);
                const newAccessToken = TokenStorage.accessToken;

                isRefreshing = false;
                processQueue(null, newAccessToken);

                if (originalRequest.headers)
                    originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                return api(originalRequest);
            } catch (err) {
                isRefreshing = false;
                processQueue(err, null);
                doLogout(false);
                return Promise.reject(err);
            }
        }

        if (isAxiosError(error)) console.error(error);
        return Promise.reject(error);
    },
);

export default api;
